/**
 * InvoiceEditModal - Modal for editing draft invoices
 */

import { useMemo } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
} from "../ui/dialog";
import { format, addDays, parseISO } from "date-fns";
import LineItemsEditor from "../LineItemsEditor";
import TotalsSummary from "../TotalsSummary";

// Payment terms configuration
const PAYMENT_TERMS = [
  { value: "due_on_receipt", label: "Due on Receipt", days: 0 },
  { value: "net_10", label: "Net 10", days: 10 },
  { value: "net_15", label: "Net 15", days: 15 },
  { value: "net_30", label: "Net 30", days: 30 },
  { value: "net_45", label: "Net 45", days: 45 },
];

export default function InvoiceEditModal({
  isOpen,
  onOpenChange,
  editFormData,
  setEditFormData,
  taxRatePercent,
  onSubmit,
  onUpdateLineItem,
  onAddLineItem,
  onRemoveLineItem,
  onCatalogSelect,
  onFormatPrice,
}) {
  // Get payment terms (default to due_on_receipt for legacy invoices)
  const paymentTerms = editFormData?.payment_terms || "due_on_receipt";

  // Compute due date based on payment terms and issue_date
  const computedDueDate = useMemo(() => {
    if (!editFormData) return new Date();
    
    const term = PAYMENT_TERMS.find(t => t.value === paymentTerms);
    const days = term ? term.days : 0;
    
    // Use issue_date if available, otherwise use created_at or today
    let issueDate;
    if (editFormData.issue_date) {
      try {
        issueDate = typeof editFormData.issue_date === 'string' 
          ? parseISO(editFormData.issue_date) 
          : editFormData.issue_date;
      } catch {
        issueDate = new Date();
      }
    } else if (editFormData.created_at) {
      try {
        issueDate = typeof editFormData.created_at === 'string'
          ? parseISO(editFormData.created_at)
          : editFormData.created_at;
      } catch {
        issueDate = new Date();
      }
    } else {
      issueDate = new Date();
    }
    
    return addDays(issueDate, days);
  }, [paymentTerms, editFormData]);

  // Format the displayed due date
  const displayDueDate = useMemo(() => {
    if (!editFormData) return "";
    
    // If there's an existing due_date and payment_terms hasn't changed, show it
    if (editFormData.due_date && editFormData.payment_terms === editFormData._original_payment_terms) {
      try {
        const existingDate = typeof editFormData.due_date === 'string'
          ? parseISO(editFormData.due_date)
          : editFormData.due_date;
        return format(existingDate, "MMM d, yyyy");
      } catch {
        // Fall through to computed
      }
    }
    return format(computedDueDate, "MMM d, yyyy");
  }, [editFormData, computedDueDate]);

  const handlePaymentTermsChange = (value) => {
    if (editFormData) {
      setEditFormData({ ...editFormData, payment_terms: value });
    }
  };

  if (!editFormData) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl" data-testid="edit-invoice-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl uppercase">
            Edit Invoice
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          <form id="edit-invoice-form" onSubmit={onSubmit} className="space-y-4">
            {/* Customer (read-only) */}
            <div>
              <Label>Customer</Label>
              <Input
                value={editFormData.customer_name}
                disabled
                className="mt-1 h-12 border-2 bg-zinc-100 text-zinc-500"
              />
            </div>

            {/* Title */}
            <div>
              <Label>Title *</Label>
              <Input
                value={editFormData.title}
                onChange={(e) => setEditFormData({ ...editFormData, title: e.target.value })}
                className="mt-1 h-12 border-2"
                required
                data-testid="edit-invoice-title"
              />
            </div>

            {/* Payment Terms + Due Date */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium">Payment Terms</Label>
                <Select
                  value={paymentTerms}
                  onValueChange={handlePaymentTermsChange}
                >
                  <SelectTrigger className="mt-1 h-12 border-2" data-testid="edit-invoice-payment-terms-select">
                    <SelectValue placeholder="Select terms" />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_TERMS.map((term) => (
                      <SelectItem key={term.value} value={term.value}>
                        {term.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-medium">Due Date</Label>
                <Input
                  value={displayDueDate}
                  disabled
                  className="mt-1 h-12 border-2 bg-zinc-50 text-zinc-600"
                  data-testid="edit-invoice-due-date-display"
                />
                <p className="text-xs text-zinc-500 mt-1">Auto-calculated from payment terms</p>
              </div>
            </div>

            <LineItemsEditor
              items={editFormData.items}
              onUpdateItem={onUpdateLineItem}
              onAddItem={onAddLineItem}
              onRemoveItem={onRemoveLineItem}
              onCatalogSelect={onCatalogSelect}
              onFormatPrice={onFormatPrice}
              taxRatePercent={taxRatePercent}
              testIdPrefix="edit-invoice-line"
            />

            {/* Notes */}
            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                value={editFormData.notes}
                onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
                placeholder="Payment terms or additional notes..."
                className="mt-1 border-2"
                rows={3}
              />
            </div>

            <TotalsSummary items={editFormData.items} taxRatePercent={taxRatePercent} />
          </form>
        </DialogBody>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="flex-1 sm:flex-none border-2"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="edit-invoice-form"
            className="flex-1 sm:flex-none bg-[#FF6600] hover:bg-[#CC5200] text-white"
            disabled={!editFormData.title}
            data-testid="save-edit-invoice-btn"
          >
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
